import { useState, useEffect, useCallback } from "react";
import { CasualProfileView } from "./CasualProfileView.jsx";
import { SkaiciuokleView, HOUSE_TYPES, SYSTEMS } from "./SkaiciuokleView.jsx";
import { DuomenuMatricaView } from "./DuomenuMatricaView.jsx";
import { KainininkasMasterView } from "./KainininkasMasterView.jsx";
import { AdminAccountsView } from "./AdminAccountsView.jsx";
import { Modal } from "../components/Modal.jsx";
import { casualData } from "../api/casualData.js";
import { RealtimeProvider } from "./realtime/RealtimeProvider.jsx";

const MENU_ITEMS = [
  { id: "pagrindinis", label: "Pagrindinis", icon: "🏠" },
  { id: "profilis", label: "Mano profilis", icon: "👤" },
  { id: "samatos", label: "Mano sąmatos", icon: "📊" },
  { id: "skaiciuokle", label: "Skaičiuoklė", icon: "🧮" },
  { id: "uzsakymai", label: "Užsakymai", icon: "📦" },
];

// Matomos tik isAdmin (master) paskyrai — rodomos meniu kolonos apačioje.
const ADMIN_MENU_ITEMS = [
  { id: "duomenu_matrica", label: "Duomenų matrica", icon: "🗂️" },
  { id: "kainininkas_master", label: "Kainininkas", icon: "💰" },
  { id: "admin_accounts", label: "Master paskyros", icon: "🔐" },
];

const ALL_SECTION_IDS = [...MENU_ITEMS, ...ADMIN_MENU_ITEMS].map(i => i.id);
const ADMIN_SECTION_IDS = ADMIN_MENU_ITEMS.map(i => i.id);

// Perkrovus puslapį (F5), programa vėl atsidaro ties ta pačia meniu skiltimi,
// kurioje vartotojas dirbo — ne visada atgal ties "Pagrindinis".
const ACTIVE_SECTION_KEY = "sannamai_casual_active_section";
function readStoredSection() {
  try { return localStorage.getItem(ACTIVE_SECTION_KEY); } catch { return null; }
}
function storeSection(id) {
  try { if (id) localStorage.setItem(ACTIVE_SECTION_KEY, id); } catch {}
}

// Tik skaitymo sąmatos peržiūra — atskirai nuo "Redaguoti", kuris atidaro
// interaktyvų Skaičiuoklė vedlį.
function EstimateViewModal({ estimate, onClose }) {
  const houseLabel = HOUSE_TYPES.find(t => t.id === estimate.houseType)?.label || "—";
  const isTwoFloors = estimate.houseType === "house2";
  const totalArea = isTwoFloors
    ? (parseFloat(estimate.area1) || 0) + (parseFloat(estimate.area2) || 0)
    : (parseFloat(estimate.area1) || 0);
  const sysLabels = (estimate.systems || []).map(id => SYSTEMS.find(s => s.id === id)?.label).filter(Boolean).join(", ") || "—";

  function renderRooms(rooms) {
    if (!rooms || rooms.length === 0) return <div style={{ color: "#9ca3af" }}>—</div>;
    return rooms.map(r => (
      <div key={r.id} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
        <span>{r.type}</span>
        <span>{(parseFloat(r.area) || 0).toFixed(2)} m²</span>
      </div>
    ));
  }

  return (
    <Modal title={`Sąmata: ${estimate.name}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: "#383853", display: "flex", flexDirection: "column", gap: 8 }}>
        <div><strong>Kodas:</strong> {estimate.code}</div>
        <div><strong>Būsto tipas:</strong> {houseLabel}</div>
        <div><strong>Bendras plotas:</strong> {totalArea.toFixed(2)} m²</div>
        <div><strong>Sistemos:</strong> {sysLabels}</div>
        <div style={{ marginTop: 6, fontWeight: 700 }}>{isTwoFloors ? "Patalpos (1 aukštas)" : "Patalpos"}</div>
        {renderRooms(estimate.roomsFloor1)}
        {isTwoFloors && (
          <>
            <div style={{ marginTop: 6, fontWeight: 700 }}>Patalpos (2 aukštas)</div>
            {renderRooms(estimate.roomsFloor2)}
          </>
        )}
      </div>
    </Modal>
  );
}

export function CasualHome({ user }) {
  const [activeSection, setActiveSectionState] = useState(() => {
    const stored = readStoredSection();
    if (!stored || !ALL_SECTION_IDS.includes(stored)) return "pagrindinis";
    if (ADMIN_SECTION_IDS.includes(stored) && !user?.isAdmin) return "pagrindinis";
    return stored;
  });
  function setActiveSection(id) {
    setActiveSectionState(id);
    storeSection(id);
  }
  const [estimates, setEstimates] = useState([]);
  const [estimatesLoaded, setEstimatesLoaded] = useState(false);
  const [editingEstimateId, setEditingEstimateId] = useState(null);
  const [viewingEstimate, setViewingEstimate] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  useEffect(() => {
    let cancelled = false;
    casualData.getEstimates()
      .then(data => { if (!cancelled) setEstimates(Array.isArray(data) ? data : []); })
      .catch(e => console.error("Failed to load estimates", e))
      .finally(() => { if (!cancelled) setEstimatesLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!estimatesLoaded) return;
    casualData.putEstimates(estimates).catch(e => console.error("Failed to save estimates", e));
  }, [estimates, estimatesLoaded]);

  // Stabilus nuorodos tipas (useCallback), kad SkaiciuokleView autosave efektas
  // galėtų saugiai naudoti šią funkciją kaip priklausomybę.
  const upsertEstimate = useCallback((estimate) => {
    setEstimates(prev => {
      const idx = prev.findIndex(e => e.id === estimate.id);
      if (idx === -1) return [...prev, estimate];
      const next = [...prev];
      next[idx] = estimate;
      return next;
    });
  }, []);

  function startNewEstimate() {
    setEditingEstimateId(null);
    setActiveSection("skaiciuokle");
  }
  function editEstimate(id) {
    setEditingEstimateId(id);
    setActiveSection("skaiciuokle");
  }

  function saveRename(id) {
    const val = renameVal.trim();
    setEstimates(prev => prev.map(e => e.id === id ? { ...e, name: val, updatedAt: new Date().toISOString() } : e));
    setRenamingId(null);
  }

  function deleteEstimate(id) {
    if (!window.confirm("Ištrinti sąmatą?")) return;
    setEstimates(prev => prev.filter(e => e.id !== id));
  }

  const content = (
    <div style={{ minHeight: "100vh", background: "#F5F6FA", display: "flex", paddingTop: 60 }}>
      {/* Vertikalus meniu */}
      <div style={{ width: 220, minWidth: 220, background: "#fff", borderRight: "1px solid #e5e7eb", display: "flex", flexDirection: "column", padding: "16px 12px", gap: 4, boxSizing: "border-box", position: "sticky", top: 60, height: "calc(100vh - 60px)", overflowY: "auto", alignSelf: "flex-start" }}>
        <button
          onClick={startNewEstimate}
          style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 12 }}
        >
          + Sukurti naują sąmatą
        </button>

        {MENU_ITEMS.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            className="hover:bg-ral3000-subtle hover:text-ral3000"
            style={{
              display: "flex", alignItems: "center", gap: 8, textAlign: "left",
              background: activeSection === item.id ? "var(--ral3000-subtle)" : "transparent",
              color: activeSection === item.id ? "var(--ral3000)" : "#383853",
              border: "none", borderRadius: 8, padding: "9px 10px",
              fontSize: 13, fontWeight: activeSection === item.id ? 700 : 600, cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 15 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}

        {user?.isAdmin && (
          <div style={{ marginTop: "auto", paddingTop: 12, borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 4 }}>
            {ADMIN_MENU_ITEMS.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className="hover:bg-ral3000-subtle hover:text-ral3000"
                style={{
                  display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                  background: activeSection === item.id ? "var(--ral3000-subtle)" : "transparent",
                  color: activeSection === item.id ? "var(--ral3000)" : "#383853",
                  border: "none", borderRadius: 8, padding: "9px 10px",
                  fontSize: 13, fontWeight: activeSection === item.id ? 700 : 600, cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 15 }}>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Turinys */}
      <div style={{ flex: 1, minWidth: 0, padding: 24 }}>
        <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          {activeSection === "pagrindinis" && (
            <>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C", marginBottom: 8 }}>
                Sveiki, {user?.email}!
              </h1>
              <p style={{ fontSize: 14, color: "#383853" }}>
                Jūsų paskyra sukurta. Daugiau funkcijų netrukus.
              </p>
            </>
          )}
          {activeSection === "profilis" && <CasualProfileView user={user} />}
          {activeSection === "samatos" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C" }}>
                  Mano sąmatos
                </h1>
                <button
                  onClick={startNewEstimate}
                  style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                >
                  + Sukurti naują sąmatą
                </button>
              </div>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f0f0f2", color: "#6b7280" }}>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Pavadinimas</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600 }}>Kodas</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {estimates.length === 0 && (
                    <tr>
                      <td colSpan="3" style={{ padding: "18px 10px", textAlign: "center", color: "#9ca3af" }}>Sąmatų dar nėra.</td>
                    </tr>
                  )}
                  {estimates.map(e => (
                    <tr key={e.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "8px 10px", fontWeight: 600, color: "#1c1c1c" }}>
                        {renamingId === e.id ? (
                          <input autoFocus value={renameVal} onChange={ev => setRenameVal(ev.target.value)}
                            onKeyDown={ev => { if (ev.key === "Enter") saveRename(e.id); if (ev.key === "Escape") setRenamingId(null); }}
                            style={{ border: "1px solid #e5e7eb", borderRadius: 5, padding: "3px 7px", fontSize: 13, fontWeight: 600, width: "100%", boxSizing: "border-box" }} />
                        ) : (
                          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {e.name || <span style={{ color: "#9ca3af", fontWeight: 400 }}>(be pavadinimo)</span>}
                            <button onClick={() => { setRenamingId(e.id); setRenameVal(e.name || ""); }} title="Pervadinti"
                              style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 12 }}>✏</button>
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#6b7280" }}>{e.code}</td>
                      <td style={{ padding: "8px 10px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                          <button onClick={() => setViewingEstimate(e)} title="Peržiūra"
                            style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>👁</button>
                          <button onClick={() => editEstimate(e.id)} title="Redaguoti"
                            style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>✏</button>
                          <button onClick={() => deleteEstimate(e.id)} title="Ištrinti"
                            style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 14 }}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {activeSection === "skaiciuokle" && (
            <SkaiciuokleView
              key={editingEstimateId || "new"}
              initialEstimate={editingEstimateId ? estimates.find(e => e.id === editingEstimateId) : null}
              onUpsert={upsertEstimate}
            />
          )}
          {activeSection === "uzsakymai" && (
            <>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C", marginBottom: 8 }}>
                Užsakymai
              </h1>
              <p style={{ fontSize: 14, color: "#383853" }}>
                Užsakymų dar nėra.
              </p>
            </>
          )}
          {activeSection === "duomenu_matrica" && user?.isAdmin && <DuomenuMatricaView />}
          {activeSection === "kainininkas_master" && user?.isAdmin && <KainininkasMasterView />}
          {activeSection === "admin_accounts" && user?.isAdmin && <AdminAccountsView user={user} />}
        </div>
      </div>

      {viewingEstimate && <EstimateViewModal estimate={viewingEstimate} onClose={() => setViewingEstimate(null)} />}
    </div>
  );

  // Realaus laiko ryšys (Kainininkas/Duomenų matrica gyvas sinchronizavimas +
  // presence) sumontuojamas VIENĄ kartą čia, ne kiekviename admin view atskirai,
  // kad ryšys išliktų naršant tarp jų. Nekurama paprastiems vartotojams, nes
  // /api/casual/realtime ir taip atmestų jų upgrade'ą (requireCasualAdmin).
  return user?.isAdmin ? <RealtimeProvider>{content}</RealtimeProvider> : content;
}
